import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Repeat, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AddComponent } from "@/components/catalogue/BundleComponentsCard";

function FiltersEditor({ product, kind, onDone }) {
  const [rows, setRows] = useState(((kind === "annual" ? product.subscription?.annual_filters : product.subscription?.filters) || []).map((f) => ({ ...f })));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/new-catalogue/subscriptions/${product.legacy_id}/filters`, { confirm: true, kind, filters: rows.map((r) => ({ product_legacy_id: r.product_legacy_id, qty: Number(r.qty) || 0 })) });
      toast.success("Filters saved"); onDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sub-filters-editor">
      <table className="w-full text-sm"><tbody>{rows.map((r, i) => (
        <tr key={r.product_legacy_id} className="border-t border-slate-100">
          <td className="py-1.5">#{r.product_legacy_id} {r.title}</td>
          <td><input type="number" min="1" className="h-8 w-20 rounded border border-slate-200 px-2" value={r.qty} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} data-testid={`sub-filter-qty-${r.product_legacy_id}`} /></td>
          <td><button type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} data-testid={`sub-filter-remove-${r.product_legacy_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button></td>
        </tr>))}</tbody></table>
      <AddComponent taken={rows.map((r) => r.product_legacy_id)} onAdd={(p) => setRows((x) => [...x, { product_legacy_id: p.legacy_id, title: p.title, qty: 1 }])} />
      <div className="mt-3 flex gap-2">
        <Button size="sm" disabled={busy} onClick={save} data-testid="sub-filters-save">{busy ? "Saving…" : "Save filters"}</Button>
        <Button size="sm" variant="ghost" onClick={() => onDone(false)} data-testid="sub-filters-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export function SubscriptionCard({ product, onSaved }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(null);
  const s = product?.subscription;
  if (product?.product_type !== "subscription" || !s) { return null; }
  const isAdmin = user?.is_super || (user?.roles || []).includes("admin");
  const filters = s.filters || [];
  const annual = s.annual_filters || [];
  const list = (fl, kind, empty) => (editing === kind ? <FiltersEditor product={product} kind={kind} onDone={(saved) => { setEditing(null); if (saved && onSaved) { onSaved(); } }} /> : fl.length ? (
    <table className="w-full text-sm"><tbody>{fl.map((f) => (
      <tr key={f.product_legacy_id} className="border-t border-slate-100" data-testid={`sub-${kind}-filter-${f.product_legacy_id}`}>
        <td className="py-1.5"><Link to={`/products/${f.product_legacy_id}`} className="text-indigo-700 hover:underline">{f.title}</Link></td>
        <td className="text-right">× {f.qty}</td>
      </tr>))}</tbody></table>
  ) : <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid={`sub-${kind}-none`}>{empty}</p>);
  return (
    <div className="ted-card p-5" data-testid="product-subscription-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Repeat className="w-4 h-4 text-violet-500" /> Subscription <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${s.site === "old" ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-800"}`} data-testid="sub-site-badge">{s.site === "old" ? "Old site" : "New site"}</span></h2>
        {isAdmin && !editing && <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setEditing("delivery")} data-testid="sub-filters-edit-btn"><Pencil className="w-4 h-4" /> {filters.length ? "Edit filters" : "Link filters"}</Button>
          <Button size="sm" variant="outline" onClick={() => setEditing("annual")} data-testid="sub-annual-edit-btn"><Pencil className="w-4 h-4" /> Yearly allocation</Button></div>}
      </div>
      <p className="mb-2 text-xs text-slate-500">Sold on the website only — not available in EPOS yet.</p>
      <div className="mb-4 flex flex-wrap gap-2" data-testid="sub-plans">
        {(s.plans || []).map((p) => <span key={p.wc_variation_id} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{p.name}: £{Number(p.price || 0).toFixed(2)}</span>)}
      </div>
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Filters per delivery (monthly at 6 &amp; 12 payments, 6-monthly each payment)</h3>
      {list(filters, "delivery", "No filters linked yet — stock can't be taken for this subscription until they are.")}
      <h3 className="mb-1 mt-4 text-sm font-semibold text-slate-700">Yearly allocation (sent after each yearly payment)</h3>
      {list(annual, "annual", "Yearly allocation not set — yearly payments won't create a job until it is.")}
    </div>
  );
}
